// Pure helpers that turn editor HTML into a branded, email-client-safe message.
// Table layout + inline styles only (Gmail strips <style> blocks).
import {
  buildBanner,
  buildSignature,
  buildFooter,
  wrapEmail,
} from "./openHouseEmailsHtml";

// TES palette (tailwind.config.js): themeColor navy + linkColor blue, so the
// emails look like the app that sent them.
const BRAND_LINK = "#218FDA";
const SLATE = "#334155";

const escapeHtml = (s) =>
  String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

// Plain-text sequence defaults → simple HTML (used until Stage 2 stores HTML).
export function plainTextToHtml(text) {
  const lines = String(text ?? "").split("\n");
  const out = [];
  let list = null; // accumulating <li>s
  let para = []; // accumulating paragraph lines

  const flushPara = () => {
    if (para.length) {
      out.push(`<p>${para.map(escapeHtml).join("<br>")}</p>`);
      para = [];
    }
  };
  const flushList = () => {
    if (list) {
      out.push(`<ul>${list.map((li) => `<li>${escapeHtml(li)}</li>`).join("")}</ul>`);
      list = null;
    }
  };

  for (const raw of lines) {
    const line = raw.trimEnd();
    const bullet = line.match(/^\s*[•\-–]\s+(.*)$/);
    if (bullet) {
      flushPara();
      (list = list || []).push(bullet[1]);
    } else if (line.trim() === "") {
      flushPara();
      flushList();
    } else {
      flushList();
      para.push(line);
    }
  }
  flushPara();
  flushList();
  return out.join("");
}

// HTML body → readable plain text (the fallback `body` field on the wire).
export function htmlToPlainText(html) {
  let s = String(html ?? "");
  s = s.replace(/<br\s*\/?>/gi, "\n");
  s = s.replace(/<li[^>]*>/gi, "• ").replace(/<\/li>/gi, "\n");
  s = s.replace(/<\/(p|div|ul|ol|h[1-6])>/gi, "\n\n");
  s = s.replace(/<[^>]+>/g, "");
  const el = typeof document !== "undefined" ? document.createElement("textarea") : null;
  if (el) {
    el.innerHTML = s;
    s = el.value;
  }
  return s.replace(/[ \t]+\n/g, "\n").replace(/\n{3,}/g, "\n\n").trim();
}

// True when a string contains real HTML markup (e.g. a body pasted straight
// from Gmail/Word) — not just bracket-shaped prose. Requires actual markup
// evidence: a closing tag (</p>, </a>, ...), an opening tag carrying a real
// attribute (style=/href=/class=/...), or a <br>/<br/> line break. Bare
// word-like brackets like "<a valid ID>", "Section <B>", "Dear <Client>" or
// {{placeholders}} never trigger it — nor does prose like "a < b" or
// "price < 500k".
const TAG_NAMES =
  "p|div|span|br|strong|em|b|i|u|a|ul|ol|li|h[1-6]|table|tbody|thead|tr|td|th|blockquote|font|img|hr";
const CLOSING_TAG_RE = new RegExp(`</(?:${TAG_NAMES})\\s*>`, "i");
const OPENING_TAG_WITH_ATTR_RE = new RegExp(`<(?:${TAG_NAMES})\\s+[^<>]*=[^<>]*/?>`, "i");
const BR_TAG_RE = /<br\s*\/?>/i;

export function looksLikeHtml(s) {
  const str = String(s ?? "");
  return CLOSING_TAG_RE.test(str) || OPENING_TAG_WITH_ATTR_RE.test(str) || BR_TAG_RE.test(str);
}

// Raw pasted HTML → clean readable template text. Anchors are converted FIRST
// so no destination is lost: <a href="URL">text</a> → "text (URL)" (just the
// URL when the visible text IS the URL, or is empty). Plain text passes
// through untouched, which also makes the function idempotent.
//
// Note: htmlToPlainText() decodes HTML entities (e.g. "&lt;br&gt;" → "<br>").
// If a template's own plain-text copy talks ABOUT html using entity-encoded
// tags, a second pass could decode those entities into real-looking markup
// and then strip it. Accepted as unrealistic for a realtor's email templates
// — nobody is writing client copy that discusses raw HTML syntax — so this
// isn't guarded against.
export function pastedHtmlToTemplateText(html) {
  const s = String(html ?? "");
  if (!looksLikeHtml(s)) return s;
  const withLinks = s.replace(
    /<a\b[^>]*\bhref\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))[^>]*>([\s\S]*?)<\/a>/gi,
    (_m, dq, sq, bare, inner) => {
      const url = (dq ?? sq ?? bare ?? "").trim();
      const text = htmlToPlainText(inner);
      if (!url) return text;
      if (!text || text === url) return url;
      return `${text} (${url})`;
    },
  );
  return htmlToPlainText(withLinks);
}

// Multi-line fields (body/intro/closing): raw pasted HTML → clean readable
// text. No-op for plain text (and for anything that isn't a string).
export function cleanPastedText(s) {
  return typeof s === "string" && looksLikeHtml(s) ? pastedHtmlToTemplateText(s) : s;
}

// Subjects are single-line: same conversion, then collapse any remaining
// newlines to spaces. The ONE place subject-cleaning happens — every screen
// that touches a subject should import this instead of rolling its own.
export function cleanPastedSubject(s) {
  return typeof s === "string" && looksLikeHtml(s)
    ? pastedHtmlToTemplateText(s).replace(/\s*\n+\s*/g, " ").trim()
    : s;
}

// A saved template doc (Firestore shape: { subject?, body?, intro?, closing?,
// ... }) with any pasted-HTML pollution cleaned out of just those four text
// fields — untouched fields (video, custom, label, category, updatedAt, ...)
// pass through as-is. Returns the SAME object reference when nothing
// changed, so callers can cheaply skip re-renders/re-saves for clean docs.
export function cleanTemplateDoc(doc) {
  if (!doc || typeof doc !== "object") return doc;
  let changed = false;
  const next = { ...doc };
  for (const field of ["subject", "body", "intro", "closing"]) {
    if (!(field in doc)) continue;
    const raw = doc[field];
    const cleaned = field === "subject" ? cleanPastedSubject(raw) : cleanPastedText(raw);
    if (cleaned !== raw) {
      next[field] = cleaned;
      changed = true;
    }
  }
  return changed ? next : doc;
}

// A {id: doc} map of saved templates (the shape SET_EMAIL_TEMPLATES carries),
// cleaned doc-by-doc. Same-reference output when nothing in the map changed.
export function cleanTemplateMap(map) {
  if (!map || typeof map !== "object") return map;
  let changed = false;
  const next = {};
  for (const [id, doc] of Object.entries(map)) {
    const cleaned = cleanTemplateDoc(doc);
    next[id] = cleaned;
    if (cleaned !== doc) changed = true;
  }
  return changed ? next : map;
}

export function youtubeThumbnail(url) {
  const m = String(url || "").match(
    /(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/)|youtu\.be\/)([\w-]{6,})/
  );
  return m ? `https://img.youtube.com/vi/${m[1]}/hqdefault.jpg` : null;
}

// Click-to-play card. Email clients can't play video inline; this is the
// industry-standard thumbnail-that-links-out.
export function videoCardHtml({ url, title, thumbnailUrl }) {
  if (!url) return "";
  const safeUrl = escapeHtml(url);
  const safeTitle = escapeHtml(title || "Watch video");
  const thumb = thumbnailUrl || youtubeThumbnail(url);
  const inner = thumb
    ? `<img src="${escapeHtml(thumb)}" alt="${safeTitle}" width="480" style="display:block;width:100%;max-width:480px;border-radius:10px;" />`
    : `<div style="background:#0f172a;border-radius:10px;padding:48px 16px;text-align:center;color:#ffffff;font-size:16px;">&#9654;&nbsp;&nbsp;${safeTitle}</div>`;
  return `
  <table role="presentation" cellpadding="0" cellspacing="0" style="margin:16px 0;width:100%;">
    <tr><td>
      <a href="${safeUrl}" target="_blank" style="text-decoration:none;color:${SLATE};">
        ${inner}
        <div style="font-size:13px;color:${BRAND_LINK};padding-top:6px;">&#9654; ${safeTitle}</div>
      </a>
    </td></tr>
  </table>`;
}

const DEFAULT_FOOTER_TEXT =
  "You're receiving this because we're working on your transaction with Team Edelstein.";

// ONE email design for the whole app: transaction emails are wrapped with the
// SAME banner / signature / footer builders the Open House emails and the
// Settings → Email Templates preview use (openHouseEmailsHtml.js) — navy
// banner with the hosted white logo + property address, agent signature with
// photo, and footer. Never a second look. `footerText` lets a caller whose
// mail isn't about an active transaction (e.g. a past-client check-in) say
// something truthful instead of the default "we're working on your
// transaction" line.
export function wrapBrandedEmail({ contentHtml, sender, videoCard, propertyAddress, footerText }) {
  return wrapEmail({
    banner: buildBanner({ propertyAddress: propertyAddress || "" }, ""),
    body: `${contentHtml || ""}${videoCard || ""}`,
    signature: buildSignature(sender || {}),
    footer: buildFooter(footerText || DEFAULT_FOOTER_TEXT),
  });
}
